import { useState, type FormEvent, type KeyboardEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { motion, AnimatePresence, type Variants } from 'framer-motion';
import { Check, Eye, EyeOff } from 'lucide-react';
import {
  completePasswordReset,
  validateResetToken,
} from '../services/api';
import { useAuthStore } from '../store/authStore';

const EASE = [0.2, 0, 0, 1] as const;

const formContainer: Variants = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.06,
      delayChildren: 0.05,
    },
  },
};

const formChild: Variants = {
  hidden: { opacity: 0, y: 6 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.28, ease: EASE },
  },
};

/**
 * Decide where to send the user after a successful reset. Mirrors
 * `LoginPage.computeHome` exactly so the freshly-reset user lands
 * on their natural home, just like a fresh login.
 */
function computeHome(
  user: import('../store/authStore').AuthUser | null,
): string {
  if (!user) return '/entries';
  if (user.role === 'debtor') return '/min-sag';
  if (
    user.role === 'lender_human' &&
    user.lender_review_status &&
    user.lender_review_status !== 'approved'
  ) {
    return '/pending-approval';
  }
  return '/entries';
}

/**
 * Public — exchange a reset token for a fresh password + auto-
 * sign-in. Reads `?token=` from the URL the email link generated.
 *
 * On mount we hit `/auth/password-reset/{token}/validate` so a
 * broken / expired link surfaces an error before the user types
 * anything. On submit we hit `/auth/password-reset/complete`,
 * drop the auto-issued JWT into `useAuthStore`, and bounce the user
 * to their natural home.
 */
export default function CompletePasswordResetPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') ?? '';

  const setSession = useAuthStore((s) => s.setSession);

  const validation = useQuery({
    queryKey: ['password-reset-validate', token],
    queryFn: () => validateResetToken(token),
    enabled: token.length > 0,
    retry: false,
  });

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [capsLockOn, setCapsLockOn] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handlePasswordKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    const native = e.nativeEvent as unknown as {
      getModifierState?: (k: string) => boolean;
    };
    if (typeof native.getModifierState === 'function') {
      setCapsLockOn(native.getModifierState('CapsLock'));
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (newPassword.length < 8) {
      setError(t('auth.passwordTooShort'));
      return;
    }
    if (newPassword !== confirmPassword) {
      setError(t('auth.passwordMismatch'));
      return;
    }
    setSubmitting(true);
    try {
      const response = await completePasswordReset(token, newPassword);
      setSession(response);
      const freshUser = useAuthStore.getState().user;
      navigate(computeHome(freshUser), { replace: true });
    } catch (_err) {
      // The backend collapses every "bad token" reason into a
      // single 404. Anything else is a transient network or server
      // error — surface generically.
      setError(t('auth.resetCompleteFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  const isInvalidToken =
    !token || (validation.isError && !validation.isLoading);

  return (
    <div className="max-w-[440px] mx-auto">
      <motion.div
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.24, ease: EASE }}
        className="space-y-3 mb-8"
      >
        <div className="section-label">{t('auth.resetSectionLabel')}</div>
        <h1 className="font-mono text-2xl tracking-tight text-[var(--color-ink)]">
          {t('auth.completeResetTitle')}
        </h1>
        {validation.data?.email && (
          <p className="text-sm text-[var(--color-ink-soft)] leading-relaxed">
            {t('auth.completeResetFor', { email: validation.data.email })}
          </p>
        )}
      </motion.div>

      <div className="draw-rule h-px mb-8" aria-hidden />

      {validation.isLoading && (
        <motion.div
          animate={{ opacity: [1, 0.5, 1] }}
          transition={{ duration: 1.2, repeat: Infinity, ease: 'easeInOut' }}
          className="font-mono text-xs uppercase tracking-[0.14em] text-[var(--color-ink-muted)]"
        >
          {t('auth.resetValidating')}
        </motion.div>
      )}

      {isInvalidToken && (
        <motion.div
          initial={{ opacity: 0, y: 4 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.22, ease: EASE }}
          className="border border-[var(--color-alert)] bg-[var(--color-alert-soft)] p-6 space-y-3"
        >
          <div className="font-mono text-[10px] uppercase tracking-[0.14em] text-[var(--color-alert)]">
            {t('auth.invalidOrExpiredResetLabel')}
          </div>
          <p className="text-sm text-[var(--color-ink)] leading-relaxed">
            {t('auth.invalidOrExpiredReset')}
          </p>
          <Link
            to="/forgot-password"
            className="inline-flex items-center gap-1.5 font-mono text-xs uppercase tracking-[0.14em] text-[var(--color-primary)] hover:text-[var(--color-primary-strong)]"
          >
            {t('auth.requestNewReset')}
          </Link>
        </motion.div>
      )}

      {!isInvalidToken && validation.isSuccess && (
        <motion.form
          variants={formContainer}
          initial="hidden"
          animate="visible"
          onSubmit={handleSubmit}
          className="space-y-6"
        >
          <motion.div variants={formChild}>
            <label
              htmlFor="new-password"
              className="section-label block mb-2"
            >
              {t('auth.newPasswordLabel')}
            </label>
            <div className="relative">
              <input
                id="new-password"
                type={passwordVisible ? 'text' : 'password'}
                autoComplete="new-password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                onKeyDown={handlePasswordKeyDown}
                className="w-full pr-10"
                required
                minLength={8}
              />
              <button
                type="button"
                onClick={() => setPasswordVisible((v) => !v)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-[var(--color-ink-muted)] hover:text-[var(--color-ink)]"
                aria-label={
                  passwordVisible
                    ? t('auth.hidePassword')
                    : t('auth.showPassword')
                }
              >
                {passwordVisible ? (
                  <EyeOff className="w-4 h-4" strokeWidth={1.5} />
                ) : (
                  <Eye className="w-4 h-4" strokeWidth={1.5} />
                )}
              </button>
            </div>
            <AnimatePresence>
              {capsLockOn && (
                <motion.div
                  key="caps-lock"
                  initial={{ opacity: 0, y: -2 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.18 }}
                  className="mt-1.5 flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.14em] text-[var(--color-alert)]"
                >
                  <span className="inline-block w-1 h-1 rounded-full bg-[var(--color-alert)]" />
                  {t('auth.capsLockOn')}
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>

          <motion.div variants={formChild}>
            <label
              htmlFor="confirm-password"
              className="section-label block mb-2"
            >
              {t('auth.confirmPasswordLabel')}
            </label>
            <input
              id="confirm-password"
              type={passwordVisible ? 'text' : 'password'}
              autoComplete="new-password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="w-full"
              required
              minLength={8}
            />
          </motion.div>

          <motion.div variants={formChild} className="flex items-center gap-3">
            <motion.button
              type="submit"
              disabled={submitting}
              className="btn-primary inline-flex items-center gap-2 px-5 py-2.5 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              animate={
                submitting ? { opacity: [1, 0.7, 1] } : { opacity: 1 }
              }
              transition={
                submitting
                  ? { duration: 1.2, repeat: Infinity, ease: 'easeInOut' }
                  : { duration: 0.15 }
              }
            >
              <Check className="w-4 h-4" strokeWidth={1.5} />
              {submitting
                ? t('auth.completeResetSubmitting')
                : t('auth.completeResetSubmit')}
            </motion.button>
          </motion.div>

          <AnimatePresence mode="wait">
            {error && (
              <motion.div
                key={error}
                role="alert"
                initial={{ opacity: 0, y: 4 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 4 }}
                transition={{ duration: 0.18, ease: EASE }}
                className="text-xs font-mono text-[var(--color-alert)]"
              >
                {error}
              </motion.div>
            )}
          </AnimatePresence>
        </motion.form>
      )}
    </div>
  );
}
